import { HttpError } from '@forto-dev/ui-platform-http'
import { Button, Grid, TextField, Typography } from '@freighthub/silhouette-core'
import { FC, FormEvent, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { useAuth } from 'service/useAuth'
import { useI18n } from 'utils/localisation'
import { ErrorBoundary } from 'view/ErrorBoundary'

import { LoginActivationError } from './LoginActivationError'
import { LoginRootCard } from './LoginRootCard'

export const getSearchParams = (): URLSearchParams => {
  const formattedParams = window.location.search.substring(1)
  return new URLSearchParams(formattedParams)
}

export type LocationState = {
  nextPathname: string
}

const LoginRoot: FC = () => {
  const { translate } = useI18n()

  const authStore = useAuth()
  const history = useHistory<LocationState>()
  const { location } = history

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const [serverError, setServerError] = useState('')
  const [isActivationError, setIsActivationError] = useState(false)

  const login = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setServerError('')
    setIsActivationError(false)

    try {
      await authStore.authenticate(email, password)
      const searchParams = getSearchParams()
      const redirectPath = searchParams.get('redirectTo')
      const nextPathname = location.state?.nextPathname
      history.replace(nextPathname ?? redirectPath ?? '/')
    } catch (error) {
      const { message, responseData } = error as HttpError
      type ResponseType = { isUserlaneEnabled?: boolean }
      setServerError(message)
      setIsActivationError(!!(responseData as ResponseType)?.isUserlaneEnabled)
    }
  }

  return (
    <ErrorBoundary>
      <LoginRootCard title={translate('login-title')}>
        <form onSubmit={login}>
          <Grid container spacing={3} direction="column">
            <Grid item>
              <Typography color="textSecondary" align="center">
                {translate('login-helper-new_to_freighthub')}
                &nbsp;
                <a href={translate('signup_url')} id="login-signup">
                  {translate('login-action-sign_up')}
                </a>
              </Typography>
            </Grid>
            <Grid item>
              <TextField
                label={translate('login-form-email')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                id="login-email"
                inputProps={{ 'data-e2e-id': 'login-email' }}
                type="email"
                fullWidth
                required
              />
            </Grid>
            <Grid item>
              <TextField
                label={translate('login-form-password')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                id="login-password"
                inputProps={{ 'data-e2e-id': 'login-password' }}
                type="password"
                fullWidth
                required
              />
            </Grid>
            <Grid item container justifyContent="space-between">
              <Grid item>
                <Typography color="textSecondary">
                  {translate('login-helper-unable_to_login')} <br />
                  <Link to="/reset-password" id="reset-password">
                    {translate('login-action-reset_password')}
                  </Link>
                </Typography>
              </Grid>
              <Grid item>
                <Button id="login-button" data-e2e-id="login-button" variant="contained" type="submit">
                  {translate('login-action-login')}
                </Button>
              </Grid>
            </Grid>
            {serverError && !isActivationError && (
              <Grid item>
                <Typography color="error">{serverError}</Typography>
              </Grid>
            )}
            {isActivationError && (
              <Grid item>
                <LoginActivationError email={email} />
              </Grid>
            )}
          </Grid>
        </form>
      </LoginRootCard>
    </ErrorBoundary>
  )
}

export default LoginRoot
